import { useQuery } from "@tanstack/react-query";

import type {
	LoadTrackingEvent,
	LoadTrackingStop,
} from "~/shared/schemas/load-tracking-schema.ts";
import { loadTrackingResponseSchema } from "~/shared/schemas/load-tracking-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

type TrackingUpdate = {
	dateTime: string;
	sortDate: string;
	status: string;
	location: string;
	distance: string;
	latitude: number | null;
	longitude: number | null;
};

type TrackingStop = {
	sortOrder: number | null;
	stopType: string;
	location: string;
	latitude: number | null;
	longitude: number | null;
	stopDate: string | null;
};

type UseGetLoadTrackingResult = {
	updates: TrackingUpdate[];
	stops: TrackingStop[];
	status: string | null;
	hideTracking: boolean | null;
	isLoading: boolean;
	isFetching: boolean;
	error: Error | null | undefined;
};

function formatDateTime(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	const month = date.getMonth() + 1;
	const day = date.getDate();
	const year = String(date.getFullYear()).slice(2);
	let hours = date.getHours();
	const minutes = String(date.getMinutes()).padStart(2, "0");
	const amPm = hours >= 12 ? "PM" : "AM";
	hours = hours % 12 || 12;

	return `${month}/${day}/${year}   ${hours}:${minutes}${amPm}`;
}

function normalizeEvent(event: LoadTrackingEvent): TrackingUpdate {
	const city = event.city?.trim() || undefined;
	const state = event.state?.trim() || undefined;
	const location = city && state ? `${city}, ${state}` : city || state || "-";

	return {
		dateTime: formatDateTime(event.locationDateTime),
		sortDate: event.locationDateTime,
		status: event.status?.trim() || "-",
		location,
		distance: event.distance == null ? "-" : String(event.distance),
		latitude: event.latitude ?? null,
		longitude: event.longitude ?? null,
	};
}

function normalizeStop(stop: LoadTrackingStop): TrackingStop {
	const city = stop.city?.trim() || undefined;
	const state = stop.state?.trim() || undefined;
	const location = city && state ? `${city}, ${state}` : city || state || "-";

	return {
		sortOrder: stop.sortOrder ?? null,
		stopType: stop.stopType,
		location,
		latitude: stop.latitude ?? null,
		longitude: stop.longitude ?? null,
		stopDate: stop.stopDate ?? null,
	};
}

type TrackingData = {
	updates: TrackingUpdate[];
	stops: TrackingStop[];
	status: string | null;
	hideTracking: boolean | null;
};

const fetchLoadTracking = async (
	poNumber: number,
	signal?: AbortSignal,
): Promise<TrackingData> => {
	// Employees can't satisfy the customer-scoped tracking endpoint (no customer
	// claims/headers), so internal builds use the employee-only endpoint.
	const route = getIsInternal()
		? apiRoutes.GETTRACKINGINTERNAL
		: apiRoutes.GETTRACKING;
	const url = route.replace(":poNumber", poNumber.toString());
	const result = await getAndValidate(
		url,
		loadTrackingResponseSchema,
		signal ? { signal } : undefined,
	);

	if (!("data" in result)) {
		throw new Error("Failed to fetch tracking data");
	}

	return {
		updates: result.data.trackingEvents.map((event) => normalizeEvent(event)),
		stops: result.data.stops.map((stop) => normalizeStop(stop)),
		status: result.data.status ?? null,
		hideTracking: result.data.hideTracking ?? null,
	};
};

export function useGetLoadTracking(
	poNumber: number | null | undefined,
): UseGetLoadTrackingResult {
	const { data, isLoading, isFetching, error } = useQuery({
		queryKey: ["loadTracking", poNumber],
		queryFn: ({ signal }) => fetchLoadTracking(Number(poNumber), signal),
		enabled: poNumber !== null && poNumber !== undefined,
		gcTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	return {
		updates: data?.updates ?? [],
		stops: data?.stops ?? [],
		status: data?.status ?? null,
		hideTracking: data?.hideTracking ?? null,
		isLoading,
		isFetching,
		error,
	};
}
